/**
 * Resolve media URLs for <img src>.
 *
 * API returns /media/tickets/... (no /api). Nginx should alias /media/ to MEDIA_ROOT.
 * Legacy JSON may use /api/media/... or admin host — normalize to same-origin /media/...
 */
const trimSlash = (s) => (s || '').replace(/\/$/, '');

let cachedServerOrigin = null;

export function setMediaPublicOrigin(origin) {
  cachedServerOrigin = origin ? trimSlash(origin) : null;
}

function legacyAdminHosts() {
  const raw = process.env.REACT_APP_LEGACY_ADMIN_MEDIA_HOSTS || 'control.vgpwonderworld.com';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isLegacyAdminHost(hostname) {
  return legacyAdminHosts().some((h) => hostname === h);
}

function getEffectiveMediaOrigin() {
  if (cachedServerOrigin) return cachedServerOrigin;
  const explicit = trimSlash(process.env.REACT_APP_MEDIA_PUBLIC_URL || '');
  if (explicit) return explicit;
  const apiBase = process.env.REACT_APP_CUSTOMER_API_URL || '';
  if (apiBase.startsWith('http')) {
    try {
      return new URL(apiBase).origin;
    } catch (e) {
      /* ignore */
    }
  }
  return '';
}

function sameOriginPath(path) {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}

export function resolveMediaUrl(url) {
  if (url == null || url === '') return null;
  const u = String(url).trim();

  if (u.startsWith('/api/media/')) {
    return sameOriginPath(`/media/${u.slice('/api/media/'.length)}`);
  }

  if (u.startsWith('/media/')) {
    return sameOriginPath(u);
  }

  if (/^https?:\/\//i.test(u)) {
    try {
      const p = new URL(u);
      const mp = getEffectiveMediaOrigin();

      if (p.pathname.startsWith('/api/media/')) {
        return `${p.origin}/media/${p.pathname.slice('/api/media/'.length)}${p.search}${p.hash}`;
      }

      if (p.pathname.startsWith('/media/')) {
        if (mp && p.origin === mp) {
          return u;
        }
        if (typeof window !== 'undefined' && p.hostname === window.location.hostname) {
          return `${window.location.origin}${p.pathname}${p.search}${p.hash}`;
        }
        if (typeof window !== 'undefined' && isLegacyAdminHost(p.hostname)) {
          const rest = p.pathname.slice('/media/'.length);
          return `${window.location.origin}/media/${rest}${p.search}${p.hash}`;
        }
        return u;
      }
    } catch (e) {
      return u;
    }
    return u;
  }

  if (!u.startsWith('/') && u.startsWith('media/')) {
    return resolveMediaUrl(`/${u}`);
  }

  if (u.startsWith('/')) {
    const mp = getEffectiveMediaOrigin();
    if (mp) {
      return `${mp}${u}`;
    }
    const apiBase = process.env.REACT_APP_CUSTOMER_API_URL || '';
    if (apiBase.startsWith('http')) {
      try {
        return new URL(u, apiBase).href;
      } catch (e) {
        /* fall through */
      }
    }
    return sameOriginPath(u);
  }

  if (/^tickets\/[^/]+\.(jpe?g|png|webp)$/i.test(u)) {
    return resolveMediaUrl(`/media/${u}`);
  }

  return u;
}
